class CategoryTileCarousel extends HTMLElement {
  connectedCallback() {
    this.scroller = this.querySelector('.category-tile-carousel__scroller');
    this.progress = this.querySelector('.category-tile-carousel__progress');
    this.progress_thumb = this.querySelector('.category-tile-carousel__progress-thumb');
    this.arrow_container = this.querySelector('.category-tile-carousel__carousel-arrows');
    this.previous_button = this.querySelector('[data-carousel-arrow="previous"]');
    this.next_button = this.querySelector('[data-carousel-arrow="next"]');
    this.layout_type = this.dataset.layoutType || 'carousel';
    this.auto_scroll_enabled = this.dataset.autoScroll === 'true' && this.layout_type === 'carousel';
    this.auto_scroll_speed = parseInt(this.dataset.autoScrollSpeed, 10) || 3000;
    this.auto_scroll_direction = this.dataset.autoScrollDirection === 'right' ? 'right' : 'left';

    if (!this.scroller || !this.progress || !this.progress_thumb) {
      return;
    }

    this.update_progress = this.update_progress.bind(this);
    this.measure_loop_width = this.measure_loop_width.bind(this);
    this.pause_auto_scroll = this.pause_auto_scroll.bind(this);
    this.resume_auto_scroll = this.resume_auto_scroll.bind(this);
    this.handle_previous_click = this.handle_previous_click.bind(this);
    this.handle_next_click = this.handle_next_click.bind(this);
    this.update_carousel_arrows = this.update_carousel_arrows.bind(this);

    this.scroller.addEventListener('scroll', this.update_progress, { passive: true });

    if (this.layout_type === 'carousel') {
      if (this.previous_button) {
        this.previous_button.addEventListener('click', this.handle_previous_click);
      }

      if (this.next_button) {
        this.next_button.addEventListener('click', this.handle_next_click);
      }
    }

    this.resize_observer = new ResizeObserver(this.update_progress);
    this.resize_observer.observe(this.scroller);

    if (this.auto_scroll_enabled) {
      this.init_auto_scroll();
    }

    requestAnimationFrame(this.update_progress);
  }

  disconnectedCallback() {
    if (this.scroller) {
      this.scroller.removeEventListener('scroll', this.update_progress);
    }

    if (this.previous_button) {
      this.previous_button.removeEventListener('click', this.handle_previous_click);
    }

    if (this.next_button) {
      this.next_button.removeEventListener('click', this.handle_next_click);
    }

    if (this.resize_observer) {
      this.resize_observer.disconnect();
    }

    this.destroy_auto_scroll();
  }

  init_auto_scroll() {
    const original_slides = Array.from(this.scroller.children);

    if (original_slides.length < 2) {
      return;
    }

    if (this.scroller.hasAttribute('data-marquee-initialized')) {
      return;
    }

    this.scroller.setAttribute('data-marquee-initialized', 'true');
    this.scroller.style.scrollSnapType = 'none';
    this.scroller.style.scrollBehavior = 'auto';

    this.original_slide_count = original_slides.length;

    original_slides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('data-marquee-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      this.scroller.appendChild(clone);
    });

    this.loop_width = 0;
    this.px_per_frame = 2000 / this.auto_scroll_speed;
    this.accumulated = 0;
    this.paused = false;

    requestAnimationFrame(this.measure_loop_width);
    window.addEventListener('resize', this.measure_loop_width);

    this.addEventListener('mouseenter', this.pause_auto_scroll);
    this.addEventListener('mouseleave', this.resume_auto_scroll);
    this.addEventListener('touchstart', this.pause_auto_scroll, { passive: true });
    this.addEventListener('touchend', this.resume_auto_scroll);
    this.addEventListener('touchcancel', this.resume_auto_scroll);

    this.tick();
  }

  destroy_auto_scroll() {
    if (this.raf_id) {
      cancelAnimationFrame(this.raf_id);
      this.raf_id = null;
    }

    window.removeEventListener('resize', this.measure_loop_width);
    this.removeEventListener('mouseenter', this.pause_auto_scroll);
    this.removeEventListener('mouseleave', this.resume_auto_scroll);
    this.removeEventListener('touchstart', this.pause_auto_scroll);
    this.removeEventListener('touchend', this.resume_auto_scroll);
    this.removeEventListener('touchcancel', this.resume_auto_scroll);
  }

  pause_auto_scroll() {
    this.paused = true;
  }

  resume_auto_scroll() {
    this.paused = false;
  }

  handle_previous_click() {
    this.scroll_carousel('previous');
  }

  handle_next_click() {
    this.scroll_carousel('next');
  }

  scroll_carousel(direction) {
    const scroll_amount = this.get_scroll_amount();
    const left = direction === 'previous' ? -scroll_amount : scroll_amount;

    this.scroller.scrollBy({
      left,
      behavior: 'smooth',
    });
  }

  get_scroll_amount() {
    const first_tile = this.scroller.querySelector('.category-tile-carousel__tile');

    if (!first_tile) {
      return this.scroller.clientWidth;
    }

    const scroller_styles = window.getComputedStyle(this.scroller);
    const column_gap = parseFloat(scroller_styles.columnGap);
    const gap = Number.isNaN(column_gap) ? 0 : column_gap;

    return first_tile.getBoundingClientRect().width + gap;
  }

  measure_loop_width() {
    const first_original = this.scroller.children[0];
    const first_clone = this.scroller.children[this.original_slide_count];

    if (!first_original || !first_clone) {
      return;
    }

    const measured_boundary = first_clone.offsetLeft - first_original.offsetLeft;
    const max_scroll = this.scroller.scrollWidth - this.scroller.clientWidth;

    this.loop_width = Math.min(measured_boundary, max_scroll);
  }

  tick() {
    if (!this.paused && this.loop_width > 0) {
      this.accumulated += this.px_per_frame;

      if (this.accumulated >= 1) {
        const px = Math.floor(this.accumulated);
        this.accumulated -= px;

        let next = this.scroller.scrollLeft + (this.auto_scroll_direction === 'right' ? -px : px);

        if (this.auto_scroll_direction === 'right' && next < 0) {
          next += this.loop_width;
        } else if (this.auto_scroll_direction === 'left' && next >= this.loop_width) {
          next -= this.loop_width;
        }

        this.scroller.scrollLeft = next;
      }
    }

    this.raf_id = requestAnimationFrame(() => this.tick());
  }

  update_progress() {
    const max_scroll = this.scroller.scrollWidth - this.scroller.clientWidth;

    this.update_carousel_arrows(max_scroll);

    if (max_scroll <= 0) {
      this.progress.hidden = true;
      return;
    }

    this.progress.hidden = false;

    const progress_width = this.progress.clientWidth;
    const thumb_width = this.progress_thumb.offsetWidth;
    const max_offset = progress_width - thumb_width;
    const scroll_ratio = this.scroller.scrollLeft / max_scroll;
    const thumb_offset = max_offset * scroll_ratio;

    this.style.setProperty('--category-tile-progress-offset', `${thumb_offset}px`);
  }

  update_carousel_arrows(max_scroll) {
    if (!this.arrow_container || !this.previous_button || !this.next_button) {
      return;
    }

    const current_max_scroll =
      typeof max_scroll === 'number' ? max_scroll : this.scroller.scrollWidth - this.scroller.clientWidth;
    const has_scroll = current_max_scroll > 1;

    this.arrow_container.hidden = !has_scroll;
    this.previous_button.disabled = !has_scroll || this.scroller.scrollLeft <= 1;
    this.next_button.disabled = !has_scroll || this.scroller.scrollLeft >= current_max_scroll - 1;
  }
}

if (!customElements.get('category-tile-carousel')) {
  customElements.define('category-tile-carousel', CategoryTileCarousel);
}
